import React from 'react';

import { Badge, Box, Divider, Flex, Paper, ScrollArea, Space, Text } from '@mantine/core';
import { useUserContext } from 'contexts/UserContext';
import { useTranslation } from 'react-i18next';
import { Conversation, TakeoverStatus } from 'shared/interfaces/Conversation';
import { ConversationHandoffStatusBadge, ConversationSourceComponent } from 'shared/utils/conversation';
import { formatDate } from 'shared/utils/format';

interface ConversationListProps {
  conversations: Conversation[];
  currentConversation: Conversation | undefined;
  setCurrentConversation: (conversation: Conversation) => void;
  forcedConversationId?: string;
  forcedConversationShowed: boolean;
  setForcedConversationShowed: (value: boolean) => void;
}

export function ConversationList({
  conversations,
  currentConversation,
  setCurrentConversation,
  forcedConversationId,
  forcedConversationShowed,
  setForcedConversationShowed
}: ConversationListProps) {
  const { t } = useTranslation();
  const [loggedInUser] = useUserContext();

  const getFirstUserMessage = (conversation: Conversation) => {
    return conversation.messages.find((message) => message.fromUser)?.text ?? '';
  };

  const getFirstSmartAssistantResponse = (conversation: Conversation) => {
    const firstUserMessageIndex = conversation.messages.findIndex((message) => message.fromUser);

    if (firstUserMessageIndex === -1 || firstUserMessageIndex === conversation.messages.length - 1) {
      return '';
    }

    for (let i = firstUserMessageIndex + 1; i < conversation.messages.length; i++) {
      const message = conversation.messages[i];
      if (!message.fromUser && message.text) {
        return message.text;
      }
    }

    return '';
  };

  const getUserLabel = (conversation: Conversation) => {
    if (typeof conversation.user !== 'string' && conversation.user?.email === loggedInUser.email) return t('APP.YOU');

    if (typeof conversation.user !== 'string' && conversation.user?.email) {
      return conversation.user.email;
    }

    return t('APP.USER');
  };

  return (
    <ScrollArea
      offsetScrollbars
      h="100%"
      w={330}
      miw={330}
      sx={(theme) => ({
        borderRight: `1px solid ${theme.colors.gray[3]}`
      })}>
      {conversations.map((conversation: Conversation, key: number) => {
        const handoffData = conversation.handoffData;
        const takeoverStatus = conversation.takeoverData?.status ?? TakeoverStatus.OFF;

        return (
          <Box key={key}>
            <Paper
              withBorder
              mt={key === 0 ? 0 : 5}
              mb={key === conversations.length - 1 ? 0 : 5}
              p="xs"
              h={76}
              maw={316}
              onClick={() => {
                setCurrentConversation(conversation);
                setForcedConversationShowed(true);
              }}
              className="g-hover-style"
              sx={(theme) => ({
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                borderColor: `${
                  currentConversation?._id === conversation._id ? theme.colors.gray[6] : undefined
                } !important`
              })}>
              <Flex direction="row" justify="space-between" align="center">
                {handoffData ? (
                  <ConversationHandoffStatusBadge status={handoffData.status} />
                ) : takeoverStatus === TakeoverStatus.ON ? (
                  <Badge color="orange" size="xs" tt="capitalize">
                    {t('SMART_ASSISTANT.CONVERSATIONS_SECTION.TAKEOVER.STATUS.ON.EXPLICIT_LABEL')}
                  </Badge>
                ) : (
                  <Text color="dimmed" truncate w={170}>
                    {t('SMART_ASSISTANT.CONVERSATIONS_SECTION.USER_TEXT', {
                      text: getFirstUserMessage(conversation),
                      userLabel: getUserLabel(conversation)
                    })}
                  </Text>
                )}

                <Text>{formatDate(conversation.createdAt)}</Text>
              </Flex>

              <Space h={5} />

              <Flex direction="row" justify="space-between" align="center" gap={5}>
                <Text truncate w="90%">
                  {t('SMART_ASSISTANT.CONVERSATIONS_SECTION.SMART_ASSISTANT_TEXT', {
                    text: getFirstSmartAssistantResponse(conversation)
                  })}
                </Text>
                <ConversationSourceComponent conversation={conversation} />
              </Flex>
            </Paper>
            {forcedConversationId === currentConversation?._id &&
              forcedConversationId === conversation._id &&
              !forcedConversationShowed && <Divider my={15} />}
          </Box>
        );
      })}
    </ScrollArea>
  );
}
